import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { fetchBwb } from '@/lib/weaviate/fetch-bwb';

export const Route = createFileRoute('/experimental/_experimentalLayout/bwb')({
  component: BwbPage,
});

type BwbSearchResult = {
  bwb_id: string;
  document_text: string;
  geldig_van: string;
  geldig_tot: string;
  chunk_id: number;
  url: string;
  _additional?: {
    certainty: number;
  };
};

function BwbPage() {
  const [searchText, setSearchText] = useState('');
  const [results, setResults] = useState<BwbSearchResult[]>([]);
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    if (!searchText.trim()) return;

    setLoading(true);
    try {
      const data = await fetchBwb(searchText);
      setResults(data as BwbSearchResult[]);
    } catch (error) {
      console.error('Error fetching results:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4">
      <Card>
        <CardHeader>
          <CardTitle>BWB documents</CardTitle>
          <CardDescription>Search for BWB documents using hybrid search.</CardDescription>
        </CardHeader>
        <CardContent className="m-auto flex flex-row gap-2 p-4">
          <Input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Enter search text..."
            className="flex-grow"
          />
          <Button onClick={handleSearch} disabled={loading} variant="default">
            {loading ? 'Searching...' : 'Search'}
          </Button>
        </CardContent>
      </Card>

      <div className="mt-4 space-y-4">
        {results.map((result, index) => (
          <Card key={index}>
            <CardHeader>
              <CardTitle>{result.bwb_id}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Geldig van</Label>
                  <div>{result.geldig_van}</div>
                </div>
                <div className="space-y-2">
                  <Label>Geldig tot</Label>
                  <div>{result.geldig_tot}</div>
                </div>
                <div className="space-y-2">
                  <Label>Chunk ID</Label>
                  <div>{result.chunk_id}</div>
                </div>
                <div className="space-y-2">
                  <Label>URL</Label>
                  <div>
                    <a href={result.url} target="_blank" rel="noopener noreferrer">
                      {result.url}
                    </a>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Certainty</Label>
                  <div>{Math.round(result._additional?.certainty ?? 0)}%</div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Document Tekst</Label>
                <Card className="bg-muted">
                  <CardContent className="p-4">
                    <pre className="whitespace-pre-wrap text-sm">{result.document_text}</pre>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
